import React, { useEffect, useCallback, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Video, X, ShieldCheck } from 'lucide-react';
import { useNotificationSocket } from '../hooks/useNotificationSocket';
import { useAuth } from '../contexts/AuthContext';
import { useButtonAlertsPendingSync } from '../contexts/ButtonAlertsPendingSyncContext';
import { playAlarmSound, primeAlarmAudioOnUserGesture } from '../utils/alarmSound';
import {
  fetchButtonAlertSound,
  isStreamFinalizedNotification,
  isStreamPersonalizedSound,
} from '../utils/buttonAlertStreamPlayback';
import {
  scheduleStreamAlertPlayback,
  scanPendingPersonalizedStreamAlerts,
} from '../utils/streamAlertAudioCoordinator';
import {
  normalizeNotificationPayload,
  notificationPayloadToNum,
  pickButtonAlertId,
  type NotificationPayload,
} from '../utils/notificationPayload';
import {
  shouldShowMapNotificationOnWeb,
} from '../utils/buttonAlertSiteLink';
import {
  applyVerifiedSiteToPayload,
  resolveMapNotificationWebVisibility,
} from '../utils/verifyButtonSiteLink';
import {
  ESCUDO_URBANO_ALERT_TITLE,
  formatRelativeTime,
  resolveEuAlarmTypeLabel,
} from '../utils/euEscudoUrbanoDisplay';
import {
  BUTTON_ALERT_COMMENT_MAX_LEN,
  BUTTON_ALERT_COMMENT_MIN_LEN,
} from '../constants/buttonAlertManagement';
import AlertAudioPlayButton from './AlertAudioPlayButton';
import './Header.css';
import './GlobalNotificationListener.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';
const CAPTCHA_LEN = 5;
const CAPTCHA_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function randomCaptchaCode(): string {
  let s = '';
  for (let i = 0; i < CAPTCHA_LEN; i++) {
    s += CAPTCHA_CHARS[Math.floor(Math.random() * CAPTCHA_CHARS.length)];
  }
  return s;
}

type GlobalAlertManageTarget = {
  buttonAlertId: number;
  toastId: string;
  siteDisplayName: string;
  siteAddress: string;
  alarmLabel: string;
  whenLabel: string;
  pressCount: number;
  message: string;
};

/** Polling hasta resolver id en BD (socket a veces llega sin buttonAlertId). */
const MAP_ALERT_ID_POLL_MS = 700;
const MAP_ALERT_ID_POLL_MAX = 36;
const MAP_ALERT_MATCH_PAST_MS = 35_000;
const MAP_ALERT_MATCH_FUTURE_MS = 120_000;

type Payload = NotificationPayload;

export { normalizeNotificationPayload } from '../utils/notificationPayload';

const baseToastStyle = {
  borderRadius: '12px',
  padding: '14px 18px',
  fontSize: '14px',
  fontWeight: '500' as const,
  color: '#fff',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
  maxWidth: '380px',
  border: 'none',
};

function isMapPathname(pathname: string): boolean {
  return pathname === '/map' || pathname.startsWith('/map/');
}

function metadataRecord(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const m = (raw as Record<string, unknown>).metadata;
  if (m && typeof m === 'object' && !Array.isArray(m)) return m as Record<string, unknown>;
  return undefined;
}

function findMatchingPendingAlertId(
  rows: unknown,
  siteId: number | undefined,
  deviceId: number | undefined,
  receivedAt: number
): number | undefined {
  if (siteId == null && deviceId == null) return undefined;
  if (!Array.isArray(rows)) return undefined;
  const minT = receivedAt - MAP_ALERT_MATCH_PAST_MS;
  const maxT = receivedAt + MAP_ALERT_MATCH_FUTURE_MS;
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const id = notificationPayloadToNum(r.id);
    const sid = notificationPayloadToNum(r.siteId);
    const did = notificationPayloadToNum(r.deviceId);
    const createdRaw = r.createdAt;
    const created =
      typeof createdRaw === 'string'
        ? Date.parse(createdRaw)
        : createdRaw instanceof Date
          ? createdRaw.getTime()
          : NaN;
    if (id == null || id <= 0 || Number.isNaN(created)) continue;
    if (created < minT || created > maxT) continue;
    if (siteId != null) {
      if (sid !== siteId) continue;
    } else if (deviceId != null && did !== deviceId) {
      continue;
    }
    if (deviceId != null && did != null && did !== deviceId) continue;
    return id;
  }
  return undefined;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const t = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(t);
      signal.removeEventListener('abort', onAbort);
      resolve();
    };
    signal.addEventListener('abort', onAbort);
  });
}

/** Toast global (fuera de /map): mismo diseño que la campana; gestión vía modal de confirmación. */
function MapGlobalAlertToast({
  toastId,
  siteDisplayName,
  siteAddress,
  alarmLabel,
  whenLabel,
  dateTimeIso,
  buttonAlertId,
  token,
  sound,
  onDismiss,
  onVerCamaras,
  onOpenManage,
  canVerCamaras,
  pressCount,
}: {
  toastId: string;
  siteDisplayName: string;
  siteAddress: string;
  alarmLabel: string;
  whenLabel: string;
  dateTimeIso: string;
  buttonAlertId?: number;
  token: string | null;
  sound?: number;
  onDismiss: () => void;
  onVerCamaras: () => void;
  onOpenManage: () => void;
  canVerCamaras: boolean;
  pressCount: number;
}) {
  const canGestionar = buttonAlertId != null && !!token;

  return (
    <div className="header-notifications-dropdown__item map-global-alert-card__root">
      <div className="header-notifications-dropdown__item-grid">
        <AlertAudioPlayButton
          buttonAlertId={buttonAlertId}
          token={token}
          sound={sound}
          size={16}
        />
        <div className="header-notifications-dropdown__item-content">
          <div className="header-notifications-dropdown__item-headline">
            <h3 className="header-notifications-dropdown__item-title-eu">{ESCUDO_URBANO_ALERT_TITLE}</h3>
            <div className="map-global-alert-card__headline-right">
              <time className="header-notifications-dropdown__item-when" dateTime={dateTimeIso}>
                {whenLabel}
              </time>
              <button
                type="button"
                className="map-global-alert-card__close"
                title="Cerrar (la alerta sigue en la campana)"
                aria-label="Cerrar notificación"
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss();
                }}
              >
                <X size={16} aria-hidden />
              </button>
            </div>
          </div>
          <div className="header-notifications-dropdown__item-block">
            <span className="header-notifications-dropdown__prov-label">Ubicación</span>
            <p className="header-notifications-dropdown__item-site-name">{siteDisplayName}</p>
            <span className="header-notifications-dropdown__prov-label">Dirección</span>
            <p className="header-notifications-dropdown__item-address">{siteAddress}</p>
          </div>
          <div className="header-notifications-dropdown__item-block">
            <span className="header-notifications-dropdown__prov-label">Tipo de alarma</span>
            <p className="header-notifications-dropdown__item-alarm-type">{alarmLabel}</p>
          </div>
          <div className="header-notifications-dropdown__item-block">
            <span className="header-notifications-dropdown__prov-label">Pulsaciones del botón</span>
            <p className="header-notifications-dropdown__item-alarm-type map-alert-press-count">
              {pressCount}×
            </p>
          </div>
        </div>
      </div>
      <div className="header-notifications-dropdown__item-actions-row">
        <button
          type="button"
          className="header-notifications-dropdown__action header-notifications-dropdown__action--primary"
          disabled={!canGestionar}
          title={
            buttonAlertId == null
              ? 'Sin id de alerta en base de datos'
              : !token
                ? 'Inicia sesión para gestionar'
                : undefined
          }
          onClick={(e) => {
            e.stopPropagation();
            onOpenManage();
          }}
        >
          Gestionar
        </button>
        <button
          type="button"
          className="header-notifications-dropdown__action"
          disabled={!canVerCamaras}
          title={!canVerCamaras ? 'Sin sitio asociado' : 'Ver cámaras del sitio'}
          onClick={(e) => {
            e.stopPropagation();
            onVerCamaras();
          }}
        >
          <Video size={14} aria-hidden />
          Ver cámaras
        </button>
      </div>
    </div>
  );
}

/**
 * Notificaciones en tiempo real (socket), con sonido según payload.
 * Las alertas de mapa con UI global solo se muestran fuera de la ruta /map.
 */
const GlobalNotificationListener: React.FC = () => {
  const { subscribe } = useNotificationSocket();
  const { token } = useAuth();
  const { notifyPendingButtonAlertsChanged } = useButtonAlertsPendingSync();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pollAbortRef = useRef<AbortController | null>(null);
  const pendingStreamScanRef = useRef<number | null>(null);
  const manageModalRef = useRef<HTMLDivElement>(null);
  const [manageTarget, setManageTarget] = useState<GlobalAlertManageTarget | null>(null);
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [manageComment, setManageComment] = useState('');
  const [manageSubmitting, setManageSubmitting] = useState(false);

  const closeManageModal = useCallback(() => {
    setManageTarget(null);
    setCaptchaInput('');
    setManageComment('');
    setCaptchaCode('');
  }, []);

  const openManageModal = useCallback((target: GlobalAlertManageTarget) => {
    setCaptchaCode(randomCaptchaCode());
    setCaptchaInput('');
    setManageComment('');
    setManageTarget(target);
  }, []);

  useEffect(() => {
    if (!manageTarget) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeManageModal();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [manageTarget, closeManageModal]);

  useEffect(() => {
    if (!manageTarget) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (manageModalRef.current?.contains(t)) return;
      closeManageModal();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [manageTarget, closeManageModal]);

  const trimmedManageComment = manageComment.trim();
  const captchaOk = captchaInput.length === CAPTCHA_LEN && captchaInput === captchaCode;
  const commentOk =
    trimmedManageComment.length >= BUTTON_ALERT_COMMENT_MIN_LEN &&
    trimmedManageComment.length <= BUTTON_ALERT_COMMENT_MAX_LEN;
  const canConfirmManage = captchaOk && commentOk && !manageSubmitting;

  const confirmManage = useCallback(async () => {
    if (!manageTarget || !token) return;
    const commentText = manageComment.trim();
    if (
      captchaInput !== captchaCode ||
      commentText.length < BUTTON_ALERT_COMMENT_MIN_LEN ||
      commentText.length > BUTTON_ALERT_COMMENT_MAX_LEN
    ) {
      return;
    }
    setManageSubmitting(true);
    const { buttonAlertId, toastId } = manageTarget;
    try {
      await axios.patch(
        `${API_URL}/button-alerts/${buttonAlertId}/acknowledge`,
        { comment: commentText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Alarma gestionada');
      notifyPendingButtonAlertsChanged();
      toast.dismiss(toastId);
      closeManageModal();
    } catch (err) {
      console.warn('[GlobalNotificationListener] acknowledge', err);
      toast.error('No se pudo registrar la gestión');
    } finally {
      setManageSubmitting(false);
    }
  }, [
    manageTarget,
    token,
    captchaInput,
    captchaCode,
    manageComment,
    closeManageModal,
    notifyPendingButtonAlertsChanged,
  ]);

  const showMapToast = useCallback(
    (p: Payload) => {
      if (!shouldShowMapNotificationOnWeb(p)) return;

      const mergedRec = p as Record<string, unknown>;
      const siteId = p.siteId != null ? Number(p.siteId) : undefined;
      const siteName = typeof p.siteName === 'string' ? p.siteName : undefined;
      const message = typeof p.message === 'string' ? p.message : undefined;
      const latitude = typeof p.latitude === 'number' ? p.latitude : undefined;
      const longitude = typeof p.longitude === 'number' ? p.longitude : undefined;
      const buttonAlertId = pickButtonAlertId(mergedRec) ?? p.buttonAlertId;

      const meta = metadataRecord(mergedRec);
      const addrRaw = meta?.site_address;
      const fromPayload =
        typeof p.siteAddress === 'string' && p.siteAddress.trim() !== '' ? p.siteAddress.trim() : '';
      const siteAddress =
        fromPayload ||
        (addrRaw != null && String(addrRaw).trim() !== '' ? String(addrRaw).trim() : '') ||
        '—';
      const aidRaw = meta?.alarm_type_id;
      const alarmTypeId = aidRaw != null && aidRaw !== '' ? Number(aidRaw) : undefined;
      const alarmLabel = resolveEuAlarmTypeLabel({
        alarmTypeId:
          alarmTypeId != null && Number.isFinite(alarmTypeId) ? alarmTypeId : undefined,
        sound: p.sound,
        message,
        alertType: typeof p.alertType === 'string' ? p.alertType : undefined,
      });
      const siteDisplayName =
        siteName?.trim() || (siteId != null ? `Sitio #${siteId}` : 'Sin nombre de sitio');
      const canVerCamaras = siteId != null || (!!siteName && siteName.trim() !== '');

      const tsIso =
        typeof p.timestamp === 'string' && p.timestamp.trim() !== '' && !Number.isNaN(Date.parse(p.timestamp))
          ? p.timestamp
          : new Date().toISOString();
      const whenLabel = formatRelativeTime(tsIso) ?? 'Ahora';

      const pcTop = notificationPayloadToNum(mergedRec.pressCount ?? mergedRec.press_count);
      const pcMeta =
        meta?.press_count != null && String(meta.press_count).trim() !== ''
          ? Number(meta.press_count)
          : NaN;
      const pressCount =
        pcTop != null && pcTop >= 1
          ? Math.min(9999, Math.floor(pcTop))
          : Number.isFinite(pcMeta) && pcMeta >= 1
            ? Math.min(9999, Math.floor(pcMeta))
            : 1;

      const alertMessage =
        typeof message === 'string' && message.trim() !== '' ? message.trim() : '—';

      toast.custom(
        (t) => (
          <MapGlobalAlertToast
            toastId={t.id}
            siteDisplayName={siteDisplayName}
            siteAddress={siteAddress}
            alarmLabel={alarmLabel}
            whenLabel={whenLabel}
            dateTimeIso={tsIso}
            buttonAlertId={buttonAlertId}
            token={token}
            sound={p.sound}
            canVerCamaras={canVerCamaras}
            pressCount={pressCount}
            onDismiss={() => toast.dismiss(t.id)}
            onVerCamaras={() => {
              navigate('/streams', {
                state: {
                  siteId,
                  siteName,
                  fromMap: true,
                  mapSessionId: Date.now(),
                  buttonAlertId,
                  latitude,
                  longitude,
                  deviceId: p.deviceId,
                },
              });
              toast.dismiss(t.id);
            }}
            onOpenManage={() => {
              if (buttonAlertId == null || !token) {
                toast.error('No se puede gestionar esta alerta');
                return;
              }
              openManageModal({
                buttonAlertId,
                toastId: t.id,
                siteDisplayName,
                siteAddress,
                alarmLabel,
                whenLabel,
                pressCount,
                message: alertMessage,
              });
            }}
          />
        ),
        {
          id: buttonAlertId != null && buttonAlertId > 0 ? `eu-map-alert-${buttonAlertId}` : undefined,
          duration: Infinity,
          style: {
            background: 'transparent',
            boxShadow: 'none',
            padding: 0,
            maxWidth: 'min(420px, calc(100vw - 20px))',
          },
        }
      );
    },
    [navigate, token, openManageModal]
  );

  const queuePendingStreamScan = useCallback(() => {
    if (!token) return;
    if (pendingStreamScanRef.current != null) {
      window.clearTimeout(pendingStreamScanRef.current);
    }
    pendingStreamScanRef.current = window.setTimeout(() => {
      pendingStreamScanRef.current = null;
      void scanPendingPersonalizedStreamAlerts(token);
    }, 450);
  }, [token]);

  const tryPlayMapAlertSound = useCallback(
    (merged: Payload) => {
      if (!shouldShowMapNotificationOnWeb(merged)) return;

      const mergedRec = merged as Record<string, unknown>;
      const streamReady = isStreamFinalizedNotification(mergedRec);
      const shouldPlayAlarm =
        streamReady ||
        (mergedRec.playAlarmSound !== false && mergedRec.play_alarm_sound !== false);

      void (async () => {
        let soundForPlay =
          typeof merged.sound === 'number' && Number.isFinite(merged.sound)
            ? merged.sound
            : undefined;

        let bid = pickButtonAlertId(mergedRec);
        if (bid == null) bid = merged.buttonAlertId;

        // Only resolve the sound from the DB when this notification did not carry
        // one. A concrete prerecorded sound (1-13) must be trusted as-is: never
        // upgrade it to a personalized stream (14) from stale/grouped metadata
        // (a prerecorded press can share an alert row with a prior personalized
        // one), or the prerecorded press would replay a previous recording.
        if (soundForPlay === undefined && bid != null && bid > 0 && token) {
          try {
            const fromApi = await fetchButtonAlertSound(bid, token);
            if (fromApi != null) soundForPlay = fromApi;
          } catch {
            /* ignore */
          }
        }

        if (isStreamPersonalizedSound(soundForPlay)) {
          if (!token) {
            // Sin token no se puede obtener el MP3 del stream: no reproducir nada.
            return;
          }
          // Sin streamFinalized: solo esperar MP3 vía poll de metadata (no martillar /audio).
          const streamAudioVersion =
            typeof merged.streamAudioVersion === 'number' &&
            Number.isFinite(merged.streamAudioVersion)
              ? merged.streamAudioVersion
              : undefined;
          if (bid != null && bid > 0) {
            scheduleStreamAlertPlayback(bid, token, {
              immediate: streamReady,
              force: streamReady,
              version: streamAudioVersion,
            });
            if (!streamReady) {
              queuePendingStreamScan();
            }
            return;
          }
          if (!shouldPlayAlarm) return;

          const siteId = merged.siteId != null ? Number(merged.siteId) : undefined;
          const deviceId = merged.deviceId != null ? Number(merged.deviceId) : undefined;
          const receivedAt = Date.now();

          for (let i = 0; i < MAP_ALERT_ID_POLL_MAX; i++) {
            try {
              const res = await axios.get<{ success?: boolean; data?: unknown[] }>(
                `${API_URL}/button-alerts/pending`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                  params: { limit: 80 },
                }
              );
              const found = findMatchingPendingAlertId(
                res.data?.data,
                siteId,
                deviceId,
                receivedAt
              );
              if (found != null) {
                scheduleStreamAlertPlayback(found, token, {
                  immediate: streamReady,
                  force: streamReady,
                  version: streamAudioVersion,
                });
                queuePendingStreamScan();
                return;
              }
            } catch {
              /* ignore */
            }
            await new Promise((r) => window.setTimeout(r, MAP_ALERT_ID_POLL_MS));
          }
          queuePendingStreamScan();
          return;
        }

        if (!shouldPlayAlarm) return;
        playAlarmSound(soundForPlay);
      })();
    },
    [token, queuePendingStreamScan]
  );

  const resolveAndShowMapToast = useCallback(
    (merged: Payload) => {
      if (!shouldShowMapNotificationOnWeb(merged)) return;

      pollAbortRef.current?.abort();
      const ac = new AbortController();
      pollAbortRef.current = ac;

      const siteId = merged.siteId != null ? Number(merged.siteId) : undefined;
      const deviceId = merged.deviceId != null ? Number(merged.deviceId) : undefined;
      const receivedAt = Date.now();

      const mergedRec = merged as Record<string, unknown>;
      let bid = pickButtonAlertId(mergedRec);
      if (bid == null) bid = merged.buttonAlertId;
      if (bid != null && bid > 0) {
        showMapToast({ ...merged, buttonAlertId: bid });
        return;
      }

      const isWorkerNotification =
        merged.mapNotificationId != null && Number(merged.mapNotificationId) > 0;
      if (isWorkerNotification) {
        return;
      }

      if (!token) {
        toast('Nueva alerta en mapa. Inicia sesión y gestiónala desde la campana.', {
          duration: 6500,
          icon: '🔔',
          style: {
            background: '#1a1a1a',
            color: '#e5e7eb',
            border: '1px solid #2a2a2a',
            boxShadow: '0 12px 28px rgba(0, 0, 0, 0.45)',
          },
        });
        return;
      }

      void (async () => {
        for (let i = 0; i < MAP_ALERT_ID_POLL_MAX; i++) {
          if (ac.signal.aborted) return;
          try {
            const res = await axios.get<{ success?: boolean; data?: unknown[] }>(
              `${API_URL}/button-alerts/pending`,
              {
                headers: { Authorization: `Bearer ${token}` },
                params: { limit: 80 },
                signal: ac.signal,
              }
            );
            const found = findMatchingPendingAlertId(res.data?.data, siteId, deviceId, receivedAt);
            if (found != null) {
              showMapToast({ ...merged, buttonAlertId: found });
              return;
            }
          } catch (e) {
            if (axios.isCancel(e)) return;
          }
          await sleep(MAP_ALERT_ID_POLL_MS, ac.signal);
        }
        if (ac.signal.aborted) return;
        toast(
          'Alerta recibida pero no se vinculó aún con el servidor. Revisa la campana para gestionarla.',
          {
            duration: 8000,
            icon: '⚠️',
            style: {
              background: '#422006',
              color: '#fef3c7',
              border: '1px solid rgba(251, 191, 36, 0.35)',
              boxShadow: '0 12px 28px rgba(0, 0, 0, 0.45)',
            },
          }
        );
      })();
    },
    [showMapToast, token]
  );

  const handlePayload = useCallback(
    (raw: unknown) => {
      const normalized = normalizeNotificationPayload(raw);
      const payload: Payload | null = normalized;
      if (!payload?.scope) return;

      const onMapPage = isMapPathname(pathname);

      const severity = (payload.severity || '').toUpperCase();
      const soundNum =
        typeof payload.sound === 'number' && Number.isFinite(payload.sound) ? payload.sound : undefined;
      const high = severity === 'HIGH' || severity === 'CRITICAL';

      if (payload.scope === 'map') {
        void (async () => {
          let merged: Payload =
            normalized != null && typeof raw === 'object' && raw !== null
              ? { ...(raw as Record<string, unknown>), ...normalized }
              : (payload as Payload);

          const visibility = await resolveMapNotificationWebVisibility(merged, raw, token);
          if (!visibility.show) return;

          if (visibility.verifiedSiteId != null) {
            merged = applyVerifiedSiteToPayload(merged, {
              verifiedSiteId: visibility.verifiedSiteId,
              verifiedSiteName: visibility.verifiedSiteName,
              verifiedSiteAddress: visibility.verifiedSiteAddress,
            });
          }

          // Do not scan/replay pending personalized stream alerts here: this runs
          // for every map notification (incl. prerecorded ones), and would replay
          // an older personalized recording out of nowhere. The personalized branch
          // inside tryPlayMapAlertSound schedules its own scan when it applies.
          tryPlayMapAlertSound(merged);
          if (!onMapPage) {
            resolveAndShowMapToast(merged);
          }
        })();
        return;
      }

      if (payload.scope === 'general' && high) {
        playAlarmSound(soundNum);
      }

      if (payload.scope === 'general') {
        const message =
          typeof payload.message === 'string'
            ? payload.message
            : payload.siteName
              ? `${payload.siteName}: ${payload.alertType || payload.message || 'Notificación'}`
              : String(payload.alertType || payload.message || 'Notificación del sistema');

        if (high) {
          toast(message, {
            icon: '⚠️',
            duration: 5000,
            style: { ...baseToastStyle, background: '#B91C1C' },
          });
        } else if (severity === 'LOW' || severity === 'MEDIUM') {
          toast.success(message, {
            duration: 4000,
            style: { ...baseToastStyle, background: '#059669' },
          });
        } else {
          toast(message, {
            duration: 4000,
            style: { ...baseToastStyle, background: '#1f2937' },
          });
        }
        return;
      }
    },
    [pathname, resolveAndShowMapToast, tryPlayMapAlertSound, queuePendingStreamScan, token]
  );

  useEffect(() => {
    return () => {
      if (pendingStreamScanRef.current != null) {
        window.clearTimeout(pendingStreamScanRef.current);
      }
    };
  }, []);

  useEffect(() => {
    primeAlarmAudioOnUserGesture();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribe<unknown>('notification', (raw) => {
      handlePayload(raw);
    });
    return unsubscribe;
  }, [subscribe, handlePayload]);

  return (
    <>
      {manageTarget &&
        createPortal(
          <div
            ref={manageModalRef}
            className="header-alerts-manage-modal__backdrop global-alert-manage-modal__backdrop"
            role="presentation"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeManageModal();
            }}
          >
            <div
              className="header-alerts-manage-modal"
              role="dialog"
              aria-labelledby="global-alert-manage-title"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="header-alerts-manage-modal__header">
                <ShieldCheck size={22} className="header-alerts-manage-modal__header-icon" aria-hidden />
                <h2 id="global-alert-manage-title" className="header-alerts-manage-modal__title">
                  Confirmar gestión
                </h2>
                <p className="header-alerts-manage-modal__subtitle">
                  Completa la verificación y un comentario para registrar que atendiste esta alerta.
                </p>
              </div>
              <div className="header-alerts-manage-modal__context">
                <div className="header-alerts-manage-modal__context-headline">
                  <span className="header-alerts-manage-modal__context-kind">{ESCUDO_URBANO_ALERT_TITLE}</span>
                  <span className="header-alerts-manage-modal__context-when">{manageTarget.whenLabel}</span>
                </div>
                <span className="header-alerts-manage-modal__context-sublabel">Ubicación</span>
                <span className="header-alerts-manage-modal__context-value">{manageTarget.siteDisplayName}</span>
                <span className="header-alerts-manage-modal__context-sublabel">Dirección</span>
                <span className="header-alerts-manage-modal__context-value">{manageTarget.siteAddress}</span>
                <span className="header-alerts-manage-modal__context-sublabel">Tipo de alarma</span>
                <span className="header-alerts-manage-modal__context-alarm">{manageTarget.alarmLabel}</span>
                <span className="header-alerts-manage-modal__context-sublabel">Pulsaciones del botón</span>
                <span className="header-alerts-manage-modal__context-alarm map-alert-press-count">
                  {manageTarget.pressCount}×
                </span>
                <span className="header-alerts-manage-modal__context-sublabel">Mensaje</span>
                <span className="header-alerts-manage-modal__context-msg">{manageTarget.message}</span>
              </div>
              <div className="header-alerts-manage-modal__field">
                <label className="header-alerts-manage-modal__label" htmlFor="global-alert-manage-captcha">
                  Código de verificación{' '}
                  <span className="header-alerts-manage-modal__hint">(copia el código)</span>
                </label>
                <div className="header-alerts-manage-modal__captcha-row" aria-hidden>
                  {captchaCode.split('').map((ch, i) => (
                    <span key={`${captchaCode}-${ch}-${i}`} className="header-alerts-manage-modal__captcha-char">
                      {ch}
                    </span>
                  ))}
                </div>
                <input
                  id="global-alert-manage-captcha"
                  className="header-alerts-manage-modal__input"
                  type="text"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={CAPTCHA_LEN}
                  placeholder={`Escribe los ${CAPTCHA_LEN} caracteres`}
                  value={captchaInput}
                  onChange={(e) => setCaptchaInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                />
                {captchaInput.length > 0 && !captchaOk && (
                  <span className="header-alerts-manage-modal__error">El código no coincide.</span>
                )}
              </div>
              <div className="header-alerts-manage-modal__field">
                <label className="header-alerts-manage-modal__label" htmlFor="global-alert-manage-comment">
                  Comentario de gestión{' '}
                  <span className="header-alerts-manage-modal__hint">
                    (mín. {BUTTON_ALERT_COMMENT_MIN_LEN}, máx. {BUTTON_ALERT_COMMENT_MAX_LEN} caracteres)
                  </span>
                </label>
                <textarea
                  id="global-alert-manage-comment"
                  className="header-alerts-manage-modal__textarea"
                  rows={4}
                  maxLength={BUTTON_ALERT_COMMENT_MAX_LEN}
                  placeholder="Describe brevemente la acción tomada…"
                  value={manageComment}
                  onChange={(e) => setManageComment(e.target.value)}
                />
                <span className="header-alerts-manage-modal__counter">
                  {trimmedManageComment.length} / {BUTTON_ALERT_COMMENT_MAX_LEN} · mín.{' '}
                  {BUTTON_ALERT_COMMENT_MIN_LEN}
                </span>
              </div>
              <div className="header-alerts-manage-modal__actions">
                <button
                  type="button"
                  className="header-alerts-manage-modal__btn header-alerts-manage-modal__btn--ghost"
                  onClick={closeManageModal}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="header-alerts-manage-modal__btn header-alerts-manage-modal__btn--primary"
                  disabled={!canConfirmManage}
                  onClick={() => void confirmManage()}
                >
                  {manageSubmitting ? 'Guardando…' : 'Confirmar gestión'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default GlobalNotificationListener;
